/** @module @liquidefy/code-editor/terminal/terminal-ansi @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  ANSISequence,
  ANSISequenceType,
  SGRAttribute,
  SGRAttributeType,
  TerminalColor,
  TerminalModeState,
  MouseTrackingMode,
  CellFlags,
  TerminalCell,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  defaultTerminalColor,
  colorFrom256,
  colorFromRGB,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 SEQUENCE PARSING — parseSequences, classifySequence, parseCSI, parseOSC, parseDCS, parseC0
// ═══════════════════════════════════════════════════════════════════════════════

export const ESCAPE = '\x1b';
export const CSI_PREFIX = '\x1b[';
export const OSC_PREFIX = '\x1b]';
export const ST = '\x1b\\';

/**
 * Parse all ANSI escape sequences from raw input.
 */
export function parseSequences(input: string): ANSISequence[] {
  const results: ANSISequence[] = [];
  let i = 0;

  while (i < input.length) {
    if (input[i] === '\x1b') {
      // Look ahead for sequence type
      if (i + 1 < input.length) {
        const next = input[i + 1]!;

        if (next === '[') {
          // CSI sequence
          const end = findCSIEnd(input, i + 2);
          const raw = input.slice(i, end + 1);
          results.push(parseCSI(raw));
          i = end + 1;
          continue;
        }

        if (next === ']') {
          // OSC sequence
          const end = findOSCEnd(input, i + 2);
          const raw = input.slice(i, end);
          results.push(parseOSC(raw));
          i = end;
          continue;
        }

        if (next === 'P') {
          // DCS sequence
          const end = findSTEnd(input, i + 2);
          const raw = input.slice(i, end);
          results.push(parseDCS(raw));
          i = end;
          continue;
        }

        // Simple escape sequences (C1 controls)
        if (next >= '@' && next <= '_') {
          results.push({
            type: 'c1',
            raw: input.slice(i, i + 2),
            params: [],
          });
          i += 2;
          continue;
        }

        // Charset designation
        if (next === '(' || next === ')') {
          if (i + 2 < input.length) {
            results.push({
              type: 'charset',
              raw: input.slice(i, i + 3),
              params: [],
              stringParam: input[i + 2],
            });
            i += 3;
            continue;
          }
        }
      }
      // Bare escape
      results.push({ type: 'c1', raw: '\x1b', params: [] });
      i++;
    } else if (input.charCodeAt(i) < 0x20) {
      // C0 control character
      results.push(parseC0(input.charCodeAt(i)));
      i++;
    } else {
      // Skip regular printable characters — not a sequence
      i++;
    }
  }

  return results;
}

/**
 * Classify a raw ANSI sequence string.
 */
export function classifySequence(raw: string): ANSISequenceType {
  if (raw.startsWith(CSI_PREFIX)) {
    // Check if it's an SGR sequence (ends with 'm')
    if (raw.endsWith('m')) return 'sgr';
    const cmd = raw[raw.length - 1]!;
    if ('ABCDEFGH'.includes(cmd)) return 'cursor';
    if ('JK'.includes(cmd)) return 'erase';
    if ('ST'.includes(cmd)) return 'scroll';
    if ('hl'.includes(cmd)) return 'mode';
    return 'csi';
  }
  if (raw.startsWith(OSC_PREFIX)) {
    if (raw.includes(';8;')) return 'hyperlink';
    if (raw.startsWith('\x1b]0;') || raw.startsWith('\x1b]2;')) return 'title';
    return 'osc';
  }
  if (raw.startsWith('\x1bP')) return 'dcs';
  if (raw.startsWith('\x1b_')) return 'apc';
  if (raw.startsWith('\x1b^')) return 'pm';
  if (raw.startsWith('\x1bX')) return 'sos';
  if (raw.length === 1 && raw.charCodeAt(0) === 7) return 'bell';
  if (raw.charCodeAt(0) < 0x20) return 'c0';
  return 'c1';
}

/**
 * Parse a CSI (Control Sequence Introducer) sequence.
 */
export function parseCSI(raw: string): ANSISequence {
  const body = raw.slice(2); // Remove ESC[
  const command = body[body.length - 1] ?? '';
  const paramStr = body.slice(0, -1);
  const params = parseCSIParams(paramStr);
  const type = classifySequence(raw);

  return { type, raw, command, params };
}

/**
 * Parse an OSC (Operating System Command) sequence.
 */
export function parseOSC(raw: string): ANSISequence {
  // Remove ESC] prefix and ST/BEL terminator
  let body = raw.slice(2);
  if (body.endsWith('\x07')) body = body.slice(0, -1);
  if (body.endsWith('\x1b\\')) body = body.slice(0, -2);

  const semicolonIdx = body.indexOf(';');
  const oscCode = semicolonIdx >= 0 ? parseInt(body.slice(0, semicolonIdx), 10) : parseInt(body, 10);
  const data = semicolonIdx >= 0 ? body.slice(semicolonIdx + 1) : '';
  const type = classifySequence(raw);

  return {
    type,
    raw,
    params: [isNaN(oscCode) ? 0 : oscCode],
    stringParam: data,
  };
}

/**
 * Parse a DCS (Device Control String) sequence.
 */
export function parseDCS(raw: string): ANSISequence {
  let body = raw.slice(2); // Remove ESC P
  if (body.endsWith('\x1b\\')) body = body.slice(0, -2);

  return {
    type: 'dcs',
    raw,
    params: [],
    stringParam: body,
  };
}

/**
 * Parse a C0 single-byte control character.
 */
export function parseC0(code: number): ANSISequence {
  const controlNames: Record<number, string> = {
    0: 'NUL', 7: 'BEL', 8: 'BS', 9: 'HT', 10: 'LF',
    11: 'VT', 12: 'FF', 13: 'CR', 14: 'SO', 15: 'SI',
    27: 'ESC',
  };
  const type: ANSISequenceType = code === 7 ? 'bell' : 'c0';
  return {
    type,
    raw: String.fromCharCode(code),
    command: controlNames[code] ?? `C0_${code}`,
    params: [code],
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 CSI COMMANDS — parseCSIParams, handleCursor, handleErase, handleScroll, handleMode, handleSGR
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Extract numeric parameters from a CSI parameter string.
 */
export function parseCSIParams(raw: string): number[] {
  if (!raw || raw.length === 0) return [];
  // Handle private mode prefix (? or >)
  const cleaned = raw.replace(/^[?>=!]/, '');
  if (cleaned.length === 0) return [];

  return cleaned.split(';').map(s => {
    const n = parseInt(s, 10);
    return isNaN(n) ? 0 : n;
  });
}

/**
 * Handle cursor movement commands, returning delta row/col.
 */
export function handleCursorCommand(
  params: number[],
  command: string
): { deltaRow: number; deltaCol: number; absolute?: { row?: number; col?: number } } {
  const n = params[0] ?? 1;

  switch (command) {
    case 'A': return { deltaRow: -n, deltaCol: 0 }; // Cursor Up
    case 'B': return { deltaRow: n, deltaCol: 0 };  // Cursor Down
    case 'C': return { deltaRow: 0, deltaCol: n };   // Cursor Forward
    case 'D': return { deltaRow: 0, deltaCol: -n };  // Cursor Back
    case 'E': return { deltaRow: n, deltaCol: 0, absolute: { col: 0 } }; // Next Line
    case 'F': return { deltaRow: -n, deltaCol: 0, absolute: { col: 0 } }; // Previous Line
    case 'G': return { deltaRow: 0, deltaCol: 0, absolute: { col: n - 1 } }; // Horizontal Absolute
    case 'H': { // Cursor Position
      const row = (params[0] ?? 1) - 1;
      const col = (params[1] ?? 1) - 1;
      return { deltaRow: 0, deltaCol: 0, absolute: { row, col } };
    }
    case 'd': return { deltaRow: 0, deltaCol: 0, absolute: { row: n - 1 } }; // Vertical Absolute
    default: return { deltaRow: 0, deltaCol: 0 };
  }
}

/**
 * Handle erase commands, returning erase specification.
 */
export function handleEraseCommand(
  params: number[],
  command: string
): { target: 'display' | 'line'; mode: number } {
  const mode = params[0] ?? 0;
  return {
    target: command === 'J' ? 'display' : 'line',
    mode,
  };
}

/**
 * Handle scroll commands.
 */
export function handleScrollCommand(
  params: number[],
  command: string
): { direction: 'up' | 'down'; lines: number } {
  const n = params[0] ?? 1;
  return {
    direction: command === 'S' ? 'up' : 'down',
    lines: n,
  };
}

/**
 * Handle mode set/reset commands.
 */
export function handleModeCommand(
  params: number[],
  _command: string,
  isSet: boolean
): Partial<TerminalModeState> {
  return handleMode(params, isSet);
}

/**
 * Handle SGR (Select Graphic Rendition) commands.
 */
export function handleSGRCommand(params: number[]): SGRAttribute[] {
  return parseSGR(params);
}

/**
 * CSI command character to handler name mapping.
 */
export const CSI_COMMAND_MAP: Record<string, string> = {
  'A': 'cursorUp',
  'B': 'cursorDown',
  'C': 'cursorForward',
  'D': 'cursorBack',
  'E': 'cursorNextLine',
  'F': 'cursorPrevLine',
  'G': 'cursorHorizontalAbsolute',
  'H': 'cursorPosition',
  'J': 'eraseDisplay',
  'K': 'eraseLine',
  'L': 'insertLines',
  'M': 'deleteLines',
  'P': 'deleteChars',
  'S': 'scrollUp',
  'T': 'scrollDown',
  'X': 'eraseChars',
  '@': 'insertChars',
  'd': 'cursorVerticalAbsolute',
  'f': 'cursorPosition',
  'h': 'setMode',
  'l': 'resetMode',
  'm': 'sgr',
  'n': 'deviceStatus',
  'r': 'setScrollRegion',
  's': 'saveCursor',
  'u': 'restoreCursor',
  't': 'windowManipulation',
};

// ═══════════════════════════════════════════════════════════════════════════════
// §4 SGR PROCESSING — parseSGR, sgrToStyle, applyStyle, resetStyle, color parsing
// ═══════════════════════════════════════════════════════════════════════════════

export const FOREGROUND_BASE = 30;
export const BACKGROUND_BASE = 40;
export const BRIGHT_OFFSET = 60;

/**
 * Parse SGR parameter list into typed SGR attributes.
 */
export function parseSGR(params: number[]): SGRAttribute[] {
  const attrs: SGRAttribute[] = [];
  if (params.length === 0) {
    attrs.push({ attribute: 'reset' });
    return attrs;
  }

  let i = 0;
  while (i < params.length) {
    const code = params[i]!;

    if (code === 0) {
      attrs.push({ attribute: 'reset' });
    } else if (code === 1) {
      attrs.push({ attribute: 'bold' });
    } else if (code === 2) {
      attrs.push({ attribute: 'dim' });
    } else if (code === 3) {
      attrs.push({ attribute: 'italic' });
    } else if (code === 4) {
      attrs.push({ attribute: 'underline' });
    } else if (code === 5) {
      attrs.push({ attribute: 'slow-blink' });
    } else if (code === 6) {
      attrs.push({ attribute: 'rapid-blink' });
    } else if (code === 7) {
      attrs.push({ attribute: 'inverse' });
    } else if (code === 8) {
      attrs.push({ attribute: 'invisible' });
    } else if (code === 9) {
      attrs.push({ attribute: 'strikethrough' });
    } else if (code === 21) {
      attrs.push({ attribute: 'double-underline' });
    } else if (code === 22) {
      attrs.push({ attribute: 'normal-intensity' });
    } else if (code === 23) {
      attrs.push({ attribute: 'not-italic' });
    } else if (code === 24) {
      attrs.push({ attribute: 'not-underline' });
    } else if (code === 25) {
      attrs.push({ attribute: 'not-blink' });
    } else if (code === 27) {
      attrs.push({ attribute: 'not-inverse' });
    } else if (code === 28) {
      attrs.push({ attribute: 'not-invisible' });
    } else if (code === 29) {
      attrs.push({ attribute: 'not-strikethrough' });
    } else if (code === 53) {
      attrs.push({ attribute: 'overline' });
    } else if (code === 55) {
      attrs.push({ attribute: 'not-overline' });
    } else if (code === 73) {
      attrs.push({ attribute: 'superscript' });
    } else if (code === 74) {
      attrs.push({ attribute: 'subscript' });
    } else if (code >= 30 && code <= 37) {
      // Standard foreground
      attrs.push({ attribute: 'foreground', value: code - 30 });
    } else if (code === 38) {
      // Extended foreground
      const parsed = parseExtendedColor(params, i + 1);
      if (parsed) {
        attrs.push({ attribute: 'foreground', value: parsed.color });
        i += parsed.consumed;
      }
    } else if (code === 39) {
      // Default foreground
      attrs.push({ attribute: 'foreground', value: defaultTerminalColor() });
    } else if (code >= 40 && code <= 47) {
      // Standard background
      attrs.push({ attribute: 'background', value: code - 40 });
    } else if (code === 48) {
      // Extended background
      const parsed = parseExtendedColor(params, i + 1);
      if (parsed) {
        attrs.push({ attribute: 'background', value: parsed.color });
        i += parsed.consumed;
      }
    } else if (code === 49) {
      // Default background
      attrs.push({ attribute: 'background', value: defaultTerminalColor() });
    } else if (code === 58) {
      // Underline color
      const parsed = parseExtendedColor(params, i + 1);
      if (parsed) {
        attrs.push({ attribute: 'underline-color', value: parsed.color });
        i += parsed.consumed;
      }
    } else if (code >= 90 && code <= 97) {
      // Bright foreground
      attrs.push({ attribute: 'foreground', value: code - 90 + 8 });
    } else if (code >= 100 && code <= 107) {
      // Bright background
      attrs.push({ attribute: 'background', value: code - 100 + 8 });
    }

    i++;
  }

  return attrs;
}

/**
 * Parse extended color (256 or 24-bit) from params starting at index.
 */
function parseExtendedColor(
  params: number[],
  index: number
): { color: TerminalColor; consumed: number } | null {
  if (index >= params.length) return null;
  const mode = params[index]!;

  if (mode === 5 && index + 1 < params.length) {
    // 256 color
    return parse256Color(params, index + 1);
  }
  if (mode === 2 && index + 3 < params.length) {
    // 24-bit color
    return parse24BitColor(params, index + 1);
  }
  return null;
}

/**
 * Parse a 256-color index from params at the given index.
 */
export function parse256Color(
  params: number[],
  index: number
): { color: TerminalColor; consumed: number } {
  const colorIndex = params[index] ?? 0;
  return {
    color: colorFrom256(colorIndex),
    consumed: 2, // mode byte + index byte
  };
}

/**
 * Parse a 24-bit RGB color from params at the given index.
 */
export function parse24BitColor(
  params: number[],
  index: number
): { color: TerminalColor; consumed: number } {
  const r = params[index] ?? 0;
  const g = params[index + 1] ?? 0;
  const b = params[index + 2] ?? 0;
  return {
    color: colorFromRGB(r, g, b),
    consumed: 4, // mode byte + r + g + b
  };
}

/**
 * Convert SGR attributes to a style object with fg, bg, and flags.
 */
export function sgrToStyle(
  attrs: SGRAttribute[]
): { fg?: TerminalColor; bg?: TerminalColor; flags: CellFlags } {
  let fg: TerminalColor | undefined;
  let bg: TerminalColor | undefined;
  let flags: CellFlags = 0;

  for (const attr of attrs) {
    switch (attr.attribute) {
      case 'reset':
        fg = undefined;
        bg = undefined;
        flags = 0;
        break;
      case 'bold': flags |= 1; break;
      case 'italic': flags |= 2; break;
      case 'underline': flags |= 4; break;
      case 'strikethrough': flags |= 8; break;
      case 'slow-blink':
      case 'rapid-blink': flags |= 16; break;
      case 'inverse': flags |= 32; break;
      case 'invisible': flags |= 64; break;
      case 'dim': flags |= 128; break;
      case 'overline': flags |= 256; break;
      case 'double-underline': flags |= 512; break;
      case 'normal-intensity': flags &= ~(1 | 128); break;
      case 'not-italic': flags &= ~2; break;
      case 'not-underline': flags &= ~(4 | 512 | 1024); break;
      case 'not-blink': flags &= ~16; break;
      case 'not-inverse': flags &= ~32; break;
      case 'not-invisible': flags &= ~64; break;
      case 'not-strikethrough': flags &= ~8; break;
      case 'not-overline': flags &= ~256; break;
      case 'foreground':
        if (typeof attr.value === 'number') {
          fg = colorFrom256(attr.value);
        } else if (attr.value && typeof attr.value === 'object') {
          fg = attr.value as TerminalColor;
        }
        break;
      case 'background':
        if (typeof attr.value === 'number') {
          bg = colorFrom256(attr.value);
        } else if (attr.value && typeof attr.value === 'object') {
          bg = attr.value as TerminalColor;
        }
        break;
    }
  }

  return { fg, bg, flags };
}

/**
 * Apply a style to a cell, returning a new cell.
 */
export function applyStyle(
  cell: TerminalCell,
  style: { fg?: TerminalColor; bg?: TerminalColor; flags: CellFlags }
): TerminalCell {
  return {
    ...cell,
    fg: style.fg ?? cell.fg,
    bg: style.bg ?? cell.bg,
    flags: style.flags,
  };
}

/**
 * Return a reset style.
 */
export function resetStyle(): { fg: TerminalColor; bg: TerminalColor; flags: CellFlags } {
  return {
    fg: defaultTerminalColor(),
    bg: defaultTerminalColor(),
    flags: 0,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 OSC HANDLING — handleOSC, parseHyperlink, parseTitle, parseClipboard, etc.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Common OSC code mappings.
 */
export const OSC_CODES: Record<number, string> = {
  0: 'setIconAndTitle',
  1: 'setIcon',
  2: 'setTitle',
  4: 'setColor',
  7: 'setCwd',
  8: 'hyperlink',
  9: 'notification',
  10: 'setForeground',
  11: 'setBackground',
  12: 'setCursorColor',
  52: 'clipboard',
  104: 'resetColor',
  110: 'resetForeground',
  111: 'resetBackground',
  112: 'resetCursorColor',
  133: 'shellIntegration',
  1337: 'iterm2',
};

/**
 * Handle an OSC sequence, returning its type and value.
 */
export function handleOSC(
  params: number[],
  data: string
): { type: string; value: string } {
  const oscCode = params[0] ?? 0;
  const type = OSC_CODES[oscCode] ?? `osc_${oscCode}`;
  return { type, value: data };
}

/**
 * Parse an OSC 8 hyperlink.
 */
export function parseHyperlink(
  params: string
): { uri: string; params: Record<string, string> } {
  // Format: params;uri
  const parts = params.split(';');
  const paramStr = parts[0] ?? '';
  const uri = parts.slice(1).join(';');

  const parsed: Record<string, string> = {};
  if (paramStr) {
    for (const pair of paramStr.split(':')) {
      const [key, val] = pair.split('=');
      if (key && val) parsed[key] = val;
    }
  }

  return { uri, params: parsed };
}

/**
 * Parse a window title from OSC data.
 */
export function parseTitle(data: string): string {
  return data;
}

/**
 * Parse clipboard data from OSC 52.
 */
export function parseClipboard(data: string): { selection: string; content: string } {
  const semicolonIdx = data.indexOf(';');
  if (semicolonIdx < 0) return { selection: 'c', content: data };
  return {
    selection: data.slice(0, semicolonIdx),
    content: data.slice(semicolonIdx + 1),
  };
}

/**
 * Parse a color query response.
 */
export function parseColorQuery(params: string): { index: number; color: string } {
  const parts = params.split(';');
  return {
    index: parseInt(parts[0] ?? '0', 10),
    color: parts[1] ?? '',
  };
}

/**
 * Parse a notification from OSC 9.
 */
export function parseNotification(data: string): { title: string; body: string } {
  const semicolonIdx = data.indexOf(';');
  if (semicolonIdx < 0) return { title: data, body: '' };
  return {
    title: data.slice(0, semicolonIdx),
    body: data.slice(semicolonIdx + 1),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 MODE MANAGEMENT — handleMode, MODE_MAP, mode query functions
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Mode number to TerminalModeState key mapping.
 */
export const MODE_MAP: Record<number, keyof TerminalModeState> = {
  1: 'applicationCursor',
  3: 'originMode',       // 132-column mode, reused as originMode key
  5: 'reverseVideo',
  6: 'originMode',
  7: 'autoWrap',
  25: 'applicationCursor', // cursor visibility (overloaded)
  47: 'alternateScreen',
  1000: 'mouseTracking',   // mouse tracking (overloaded — value handled separately)
  1002: 'mouseTracking',
  1003: 'mouseTracking',
  1004: 'focusTracking',
  1006: 'mouseTracking',
  1015: 'mouseTracking',
  1047: 'alternateScreen',
  1049: 'alternateScreen',
  2004: 'bracketedPaste',
};

/**
 * Handle DEC private mode set/reset, returning partial state.
 */
export function handleMode(
  params: number[],
  isSet: boolean
): Partial<TerminalModeState> {
  const result: { -readonly [K in keyof TerminalModeState]?: TerminalModeState[K] } = {};

  for (const code of params) {
    switch (code) {
      case 1:
        result.applicationCursor = isSet;
        break;
      case 5:
        result.reverseVideo = isSet;
        break;
      case 6:
        result.originMode = isSet;
        break;
      case 7:
        result.autoWrap = isSet;
        break;
      case 4:
        result.insertMode = isSet;
        break;
      case 47:
      case 1047:
      case 1049:
        result.alternateScreen = isSet;
        break;
      case 1000:
        result.mouseTracking = isSet ? 'normal' : 'none';
        break;
      case 1002:
        result.mouseTracking = isSet ? 'button' : 'none';
        break;
      case 1003:
        result.mouseTracking = isSet ? 'any' : 'none';
        break;
      case 1004:
        result.focusTracking = isSet;
        break;
      case 1006:
        result.mouseTracking = isSet ? 'sgr' : 'none';
        break;
      case 1015:
        result.mouseTracking = isSet ? 'urxvt' : 'none';
        break;
      case 2004:
        result.bracketedPaste = isSet;
        break;
      case 9:
        result.mouseTracking = isSet ? 'x10' : 'none';
        break;
    }
  }

  return result;
}

/**
 * Check if application cursor mode is enabled.
 */
export function isApplicationCursorMode(state: TerminalModeState): boolean {
  return state.applicationCursor;
}

/**
 * Check if bracketed paste mode is enabled.
 */
export function isBracketedPasteMode(state: TerminalModeState): boolean {
  return state.bracketedPaste;
}

/**
 * Check if auto wrap mode is enabled.
 */
export function isAutoWrapMode(state: TerminalModeState): boolean {
  return state.autoWrap;
}

/**
 * Check if any mouse tracking mode is enabled.
 */
export function isMouseTrackingEnabled(state: TerminalModeState): boolean {
  return state.mouseTracking !== 'none';
}

/**
 * Return the human-readable name for a mouse tracking mode.
 */
export function mouseTrackingModeName(mode: MouseTrackingMode): string {
  const names: Record<MouseTrackingMode, string> = {
    'none': 'None',
    'x10': 'X10 Compatibility',
    'normal': 'Normal Tracking',
    'button': 'Button Event Tracking',
    'any': 'Any Event Tracking',
    'sgr': 'SGR Extended',
    'urxvt': 'URXVT Extended',
  };
  return names[mode];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 SEQUENCE GENERATION — cursor, erase, color, attribute, title sequences
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generate CSI sequence to move cursor to (row, col) (1-based).
 */
export function cursorTo(row: number, col: number): string {
  return `\x1b[${row + 1};${col + 1}H`;
}

/**
 * Move cursor up by n lines.
 */
export function cursorUp(n: number): string {
  return n === 1 ? '\x1b[A' : `\x1b[${n}A`;
}

/**
 * Move cursor down by n lines.
 */
export function cursorDown(n: number): string {
  return n === 1 ? '\x1b[B' : `\x1b[${n}B`;
}

/**
 * Move cursor forward (right) by n columns.
 */
export function cursorForward(n: number): string {
  return n === 1 ? '\x1b[C' : `\x1b[${n}C`;
}

/**
 * Move cursor back (left) by n columns.
 */
export function cursorBack(n: number): string {
  return n === 1 ? '\x1b[D' : `\x1b[${n}D`;
}

/**
 * Save cursor position.
 */
export function saveCursor(): string {
  return '\x1b7';
}

/**
 * Restore cursor position.
 */
export function restoreCursor(): string {
  return '\x1b8';
}

/**
 * Erase display (mode: 0=below, 1=above, 2=all, 3=scrollback).
 */
export function eraseDisplay(mode?: number): string {
  return `\x1b[${mode ?? 0}J`;
}

/**
 * Erase line (mode: 0=to end, 1=to start, 2=entire line).
 */
export function eraseLine(mode?: number): string {
  return `\x1b[${mode ?? 0}K`;
}

/**
 * Set foreground and/or background color using SGR.
 */
export function setColor(fg?: number, bg?: number): string {
  const parts: number[] = [];
  if (fg !== undefined) {
    if (fg < 8) parts.push(30 + fg);
    else if (fg < 16) parts.push(90 + fg - 8);
    else parts.push(38, 5, fg);
  }
  if (bg !== undefined) {
    if (bg < 8) parts.push(40 + bg);
    else if (bg < 16) parts.push(100 + bg - 8);
    else parts.push(48, 5, bg);
  }
  return parts.length > 0 ? `\x1b[${parts.join(';')}m` : '';
}

/**
 * Reset all colors to default.
 */
export function resetColor(): string {
  return '\x1b[39;49m';
}

/**
 * Set bold.
 */
export function setBold(): string {
  return '\x1b[1m';
}

/**
 * Set italic.
 */
export function setItalic(): string {
  return '\x1b[3m';
}

/**
 * Set underline.
 */
export function setUnderline(): string {
  return '\x1b[4m';
}

/**
 * Reset all attributes.
 */
export function resetAttributes(): string {
  return '\x1b[0m';
}

/**
 * Set the terminal window title via OSC 2.
 */
export function setTitle(title: string): string {
  return `\x1b]2;${title}\x07`;
}

/**
 * Create a hyperlink via OSC 8.
 */
export function setHyperlink(uri: string, text: string): string {
  return `\x1b]8;;${uri}\x07${text}\x1b]8;;\x07`;
}

/**
 * Request cursor position (DECXCPR).
 */
export function requestCursorPosition(): string {
  return '\x1b[6n';
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Find the end of a CSI sequence (the final byte in 0x40-0x7e range).
 */
function findCSIEnd(input: string, start: number): number {
  for (let i = start; i < input.length; i++) {
    const code = input.charCodeAt(i);
    if (code >= 0x40 && code <= 0x7e) return i;
  }
  return input.length - 1;
}

/**
 * Find the end of an OSC sequence (BEL or ST).
 */
function findOSCEnd(input: string, start: number): number {
  for (let i = start; i < input.length; i++) {
    if (input[i] === '\x07') return i + 1;
    if (input[i] === '\x1b' && i + 1 < input.length && input[i + 1] === '\\') {
      return i + 2;
    }
  }
  return input.length;
}

/**
 * Find the ST (String Terminator) end.
 */
function findSTEnd(input: string, start: number): number {
  for (let i = start; i < input.length; i++) {
    if (input[i] === '\x1b' && i + 1 < input.length && input[i + 1] === '\\') {
      return i + 2;
    }
  }
  return input.length;
}
